import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { billingApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import CouponApplication from '@/components/subscription/CouponApplication'
import { formatCurrency, formatDate } from '@/utils/format'
import { CreditCard, Receipt, DollarSign, Coins, ExternalLink, Users } from 'lucide-react'

export default function Billing() {
  const { isSuperAdmin } = useAuth()
  const [viewAll, setViewAll] = useState(() => {
    // Restore from localStorage if super admin
    if (isSuperAdmin()) {
      const saved = localStorage.getItem('billing_view_all')
      return saved === 'true'
    }
    return false
  })

  // Persist viewAll to localStorage
  useEffect(() => {
    if (isSuperAdmin()) {
      localStorage.setItem('billing_view_all', String(viewAll))
    }
  }, [viewAll, isSuperAdmin])

  const { data, isLoading } = useQuery({
    queryKey: ['billing-history', viewAll],
    queryFn: () => billingApi.getHistory(viewAll),
  })

  if (isLoading) return <Loading />

  const payments = (data?.data as any)?.payments || []
  const summary = (data?.data as any)?.summary || {
    total_spent: 0,
    total_subscription_payments: 0,
    total_coin_purchases: 0,
    payment_count: 0
  }

  const getPaymentTypeIcon = (type: string) => {
    switch (type) {
      case 'subscription':
      case 'charge':
        return <CreditCard className="w-5 h-5 text-blue-600" />
      case 'coin_purchase':
        return <Coins className="w-5 h-5 text-yellow-600" />
      default:
        return <Receipt className="w-5 h-5 text-gray-600" />
    }
  }

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'subscription':
        return <Badge variant="info">Subscription</Badge>
      case 'charge':
        return <Badge variant="info">Payment</Badge>
      case 'coin_purchase':
        return <Badge variant="warning">Coin Purchase</Badge>
      default:
        return <Badge variant="gray">{type}</Badge>
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing History</h1>
          <p className="text-gray-600 mt-1">
            {viewAll ? 'Viewing all users\' payments and transactions' : 'View all your payments and transactions'}
          </p>
        </div>
        {isSuperAdmin() && (
          <button
            onClick={() => setViewAll(!viewAll)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              viewAll
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Users className="w-5 h-5" />
            <span>{viewAll ? 'View My Billing' : 'View All Users'}</span>
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 mb-2">Total Spent</p>
              <p className="text-4xl font-bold">{formatCurrency(summary.total_spent)}</p>
              <p className="text-blue-100 mt-1 text-sm">{summary.payment_count} payments</p>
            </div>
            <DollarSign className="w-16 h-16 text-blue-300" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 mb-2">Subscription Payments</p>
              <p className="text-4xl font-bold">{formatCurrency(summary.total_subscription_payments)}</p>
              <p className="text-purple-100 mt-1 text-sm">Monthly fees</p>
            </div>
            <CreditCard className="w-16 h-16 text-purple-300" />
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 mb-2">Coin Purchases</p>
              <p className="text-4xl font-bold">{formatCurrency(summary.total_coin_purchases)}</p>
              <p className="text-yellow-100 mt-1 text-sm">Barter Coins</p>
            </div>
            <Coins className="w-16 h-16 text-yellow-300" />
          </div>
        </Card>
      </div>

      {/* Coupon Application - Only for non-admin users */}
      {!viewAll && <CouponApplication />}

      {/* Payment History */}
      <Card title="Payment History">
        {payments.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No payments yet</p>
            <p className="text-gray-400 text-sm mt-2">Your payment history will appear here</p>
          </div>
        ) : (
          <ResponsiveTable>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  {viewAll && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {payments.map((payment: any, index: number) => (
                  <tr key={payment.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(payment.created_at)}
                    </td>
                    {viewAll && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{payment.user_name || '-'}</p>
                          <p className="text-xs text-gray-500">{payment.user_email || '-'}</p>
                        </div>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getPaymentTypeIcon(payment.type)}
                        {getPaymentTypeBadge(payment.type)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <p className="font-medium">{payment.description}</p>
                        {payment.type === 'coin_purchase' && payment.details && (
                          <p className="text-xs text-gray-500 mt-1">
                            {payment.details.coins} coins ({formatCurrency(payment.details.base_cost)} + {formatCurrency(payment.details.fee)} fee)
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-bold text-gray-900">
                        {formatCurrency(payment.amount)}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">
                        {payment.currency}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={payment.status === 'succeeded' || payment.status === 'completed' ? 'success' : 'gray'}>
                        {payment.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {payment.receipt_url ? (
                        <a
                          href={payment.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 inline-flex items-center space-x-1 text-sm"
                        >
                          <span>View</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        )}
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-2">Billing Information:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>All payments are securely processed by Stripe</li>
            <li>Subscription payments are charged monthly</li>
            <li>Receipts are available for download from Stripe</li>
            <li>Contact support if you have any billing questions</li>
          </ul>
        </div>
      </Card>
    </div>
  )
}
